import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    setTicketDepartment,
    setTicketInfo,
    setTicketList,
    setTicketLoading,
    setTicketPageNumber,
    setTicketPriority,
    setTicketStatus,
    setTicketTitle,
    setTicketId,
    setTicketTotalPages,
    ticketListSelector,
    useAppDispatch,
    useAppSelector,
    useGetTicketsListQuery,
} from 'app/store';

const deduplicateById = (tickets: any[]) => {
    const map = new Map<number, any>();
    for (const ticket of tickets) {
        map.set(ticket.id, ticket);
    }
    return Array.from(map.values());
};

const useTicketList = (props: any) => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const ticketList = useAppSelector(ticketListSelector);

    const [panelIndex, setPanelIndex] = useState<number | boolean>(false);

    const { routing, search, onSearch, page, onPage } = props;

    const { data, isFetching, isSuccess } = useGetTicketsListQuery({
        search,
        page,
    });

    const memoizedTickets = ticketList;

    const [totalPages, setTotalPages] = useState<number>(1);
    const [scrollPosition, setScrollPosition] = useState<number>(0);
    const [newestTicketId, setNewestTicketId] = useState<number>(
        memoizedTickets ? memoizedTickets[0]?.id : 0,
    );

    const containerRef = useRef<HTMLDivElement>(null);

    const handleUpdateTicketHeader = (ticket: any) => {
        dispatch(setTicketTitle(ticket?.title));
        dispatch(setTicketId(ticket?.id));
        dispatch(setTicketStatus(ticket?.status));
        dispatch(setTicketDepartment(ticket?.department));
        dispatch(setTicketPriority(ticket?.priority));
    };


    useEffect(() => {
        data?.items?.length > 0 && dispatch(setTicketList(data?.items));
    }, [data?.items]);

    useEffect(() => {
        if (isSuccess && !isFetching) {
            if (
                !ticketList ||
                (ticketList && ticketList[0]?.id !== data?.items[0]?.id)
            ) {
                const currentPage = data?.pagination?.current_page;

                dispatch(
                    setTicketList(
                        currentPage !== 1 && memoizedTickets
                            ? deduplicateById([
                                  ...memoizedTickets,
                                  ...data?.items,
                              ])
                            : data?.items,
                    ),
                );
                onPage(currentPage);
                setTotalPages(data?.pagination?.total_pages);
            }
        }
    }, [data, isSuccess, isFetching]);

    useEffect(() => {
        if (ticketList) {
            ticketList?.map((ticket, i) => {
                if (ticket?.id === Number(ticketId)) {
                    setPanelIndex(i);
                    handleUpdateTicketHeader(ticket);
                }
            });
        }
    }, [ticketList, ticketId]);

    const handleChangeMenu = (
        event: React.SyntheticEvent,
        newValue: number,
    ) => {
        if (ticketList && ticketList[newValue]?.id) {
            setPanelIndex(newValue);
            handleUpdateTicketHeader(ticketList[newValue]);
            dispatch(setTicketInfo(undefined));
            dispatch(setTicketStatus(undefined));
            dispatch(setTicketDepartment(undefined));
            dispatch(setTicketPriority(undefined));
            dispatch(setTicketPageNumber(1));
            dispatch(setTicketTotalPages(1));
            dispatch(setTicketLoading(true));
            navigate(
                props?.routing
                    ? `${props?.routing}/${ticketList[newValue]?.id}`
                    : `/ticket/${ticketList[newValue]?.id}`,
            );
        }
    };

    const onScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            const { target } = event;

            if (
                target instanceof HTMLDivElement &&
                target.scrollTop + target.clientHeight >=
                    target.scrollHeight - 1 &&
                !isFetching &&
                page < totalPages
            ) {
                setNewestTicketId(memoizedTickets ? memoizedTickets[0]?.id : 0);
                onPage((prevPage: number) => prevPage + 1);
            }
        },
        [isFetching, page, totalPages, memoizedTickets],
    );

    useEffect(() => {
        if (memoizedTickets && newestTicketId !== memoizedTickets[0]?.id)
            handleScrollToPosition(0);
    }, [memoizedTickets]);

    const handleScrollToPosition = (position: number) => {
        const divElement = containerRef.current;

        if (divElement) {
            divElement.scrollTop = position;
            setScrollPosition(position);
        }
    };

    const handleLoadMoreTickets = useCallback(() => {
        if (page < totalPages) {
            setNewestTicketId(memoizedTickets ? memoizedTickets[0]?.id : 0);
            onPage((prevPage: number) => prevPage + 1);
        }
    }, [page, totalPages, memoizedTickets]);

    return {
        ticketsList: memoizedTickets,
        panelIndex,
        onChangeMenu: handleChangeMenu,
        onLoadMoreTickets: handleLoadMoreTickets,
        currentPage: page,
        totalPages,
        isLoadingNewTickets: isFetching,
        scrollPosition,
        containerRef,
        onScroll,
    };
};

export default useTicketList;
